// src/components/Navigation/Navigation.tsx - Add UserMenu
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import UserMenu from '../Auth/UserMenu'; // This path is correct if UserMenu.tsx is in src/components/Auth/
import './Navigation.css';

// SVG Icon Components (keep your existing icons)
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const SportsSoccerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12v-2a5 5 0 0 0-5-5H8a5 5 0 0 0-5 5v2" />
    <circle cx="12" cy="16" r="5" />
    <line x1="12" y1="11" x2="12" y2="16" />
    <line x1="9" y1="13" x2="12" y2="16" />
    <line x1="15" y1="13" x2="12" y2="16" />
  </svg>
);

const SearchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const HistoryIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const EmojiEventsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="6" />
    <path d="M5 14h14M8 22h8" />
    <path d="M12 14v8" />
  </svg>
);

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

const Navigation: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Check auth status
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');
    setIsAuthenticated(!!(token && user));
  }, [location]);

  const menuItems: MenuItem[] = [
    { text: 'Predict', icon: <AnalyticsIcon />, path: '/' },
    // { text: 'Teams', icon: <SearchIcon />, path: '/teams' },
    // { text: 'Matches', icon: <HistoryIcon />, path: '/matches' },
    // { text: 'Leaderboard', icon: <EmojiEventsIcon />, path: '/leaderboard' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      setMobileOpen(false);
    }
  };

  const isActivePath = (path: string): boolean => {
    return location.pathname === path;
  };

  // Don't show navigation if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-content">
            {/* Desktop Logo */}
            <div className="logo-desktop">
              <SportsSoccerIcon />
              <Link to="/" className="logo-text">
                ZiTech AI
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-button"
              onClick={handleDrawerToggle}
              aria-label="Open menu"
            >
              <MenuIcon />
            </button>

            {/* Mobile Logo */}
            <Link to="/" className="logo-mobile">
              ZiTech AI
            </Link>

            {/* Mobile Icon */}
            <div className="mobile-icon">
              <SportsSoccerIcon />
            </div>

            {/* Desktop Menu */}
            <div className="desktop-menu">
              {menuItems.map((item) => (
                <Link
                  key={item.text}
                  to={item.path}
                  className={`desktop-menu-item ${isActivePath(item.path) ? 'active' : ''}`}
                >
                  <span className="menu-item-icon">{item.icon}</span>
                  <span>{item.text}</span>
                </Link>
              ))}
            </div>

            {/* User Menu */}
            <UserMenu />
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <div className="drawer-overlay" onClick={handleDrawerToggle} />
      )}

      {/* Mobile Drawer */}
      <div className={`mobile-drawer ${mobileOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h3 className="drawer-title">ZiTech AI</h3>
        </div>
        
        <nav className="drawer-nav">
          {menuItems.map((item) => (
            <Link
              key={item.text}
              to={item.path}
              className={`drawer-menu-item ${isActivePath(item.path) ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <span className="drawer-item-icon">{item.icon}</span>
              <span className="drawer-item-text">{item.text}</span>
            </Link>
          ))}
          
          {/* Mobile User Info */}
          <div className="drawer-divider"></div>
          <Link
            to="/profile"
            className="drawer-menu-item"
            onClick={handleLinkClick}
          >
            <span className="drawer-item-icon">👤</span>
            <span className="drawer-item-text">Profile</span>
          </Link>
          <Link
            to="/settings"
            className="drawer-menu-item"
            onClick={handleLinkClick}
          >
            <span className="drawer-item-icon">⚙️</span>
            <span className="drawer-item-text">Settings</span>
          </Link>
        </nav>
      </div>
    </>
  );
};

export default Navigation;